import {
  PointToPointTransportCostBreakdownResultDTO,
  TransitTimeEstimateResultDTO,
  TransitTimeSourceExplanation,
} from 'api';
import { useFormContext } from 'form';
import { Alert, Box, formatCurrency, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from 'ui';
import { HelpOutlineOutlined } from 'ui/icons';

import { displayTransportCostSource } from '~/transport-costs/utils';

import { MissingCostsAlert } from '../MissingCostsAlert/MissingCostsAlert';

const UNKNOWN = 'N/A';

const sourceExplanationEnumValueToString = (enumValue: TransitTimeSourceExplanation): string => {
  switch (enumValue) {
    case TransitTimeSourceExplanation.FULL_MATCH:
      return 'Based on historical quotes matching exactly.';
    case TransitTimeSourceExplanation.INCOTERMS_MISMATCH:
      return 'Based on historical quotes where incoterms might not match the request.';
    case TransitTimeSourceExplanation.ORIGIN_COUNTRY_MISMATCH:
      return 'Based on historical quotes where origin country might not match the requested origin, but port of loading matches exactly.';
    case TransitTimeSourceExplanation.DESTINATION_COUNTRY_MISMATCH:
      return 'Based on historical quotes where destination country might not match the requested destination, but port of discharge matches exactly.';
    case TransitTimeSourceExplanation.BOTH_ORIGIN_AND_DESTINATION_COUNTRY_MISMATCH:
      return 'Based on historical quotes where both origin and destination countries might not match the request, but ports of loading and discharge match exactly.';
    case TransitTimeSourceExplanation.POL_MISMATCH:
      return "Based on historical quotes where port of loading might not match the requested port, but country of the port matches the requested port's country.";
    case TransitTimeSourceExplanation.POD_MISMATCH:
      return "Based on historical quotes where port of discharge might not match the requested port, but country of the port matches the requested port's country.";
    case TransitTimeSourceExplanation.BOTH_POL_AND_POD_MISMATCH:
      return "Based on historical quotes where both port of loading and port of discharge might not match the request, but countries of the ports match the requested ports' countries.";
    case TransitTimeSourceExplanation.CONTINENT_TO_CONTINENT_APPROXIMATION:
      return 'Based on pre-defined corridors between continents, only provided as a very rough fallback estimate in case other options are not available.';
    case TransitTimeSourceExplanation.ROAD_PER_KM_ESTIMATE:
      return 'Based on the rough estimation of what distance a truck can typically cover in a day.';
    default:
      return '';
  }
};

export const TransportCostDetails = () => {
  const form = useFormContext();
  const {
    watch,
    formState: { errors },
  } = form;
  const tonnes = watch('tonnes');
  const tonnesPerLoad = watch('tonnes_per_load');
  const originAddress = watch('origin_address');
  const destinationAddress = watch('destination_address');
  const transportPrice: PointToPointTransportCostBreakdownResultDTO | undefined = watch('transport_price');
  const transitTimeEstimate: TransitTimeEstimateResultDTO | undefined = watch('transit_time_estimate');

  const displayedShippingCostPerLoad = formatCurrency(transportPrice?.total_cost_in_target_currency || 0, {
    currencyCode: transportPrice?.target_currency,
  });
  const displayedTotalShippingCost = formatCurrency(
    (Number.parseInt(transportPrice?.total_cost_in_target_currency || '0') * (tonnes || 0)) / (tonnesPerLoad || 1),
    {
      currencyCode: transportPrice?.target_currency,
    }
  );

  const displayedOriginRoadCostSource = transportPrice?.origin_road_cost?.source
    ? displayTransportCostSource(transportPrice.origin_road_cost.source)
    : UNKNOWN;
  const displayedDestinationRoadCostSource = transportPrice?.destination_road_cost?.source
    ? displayTransportCostSource(transportPrice.destination_road_cost.source)
    : UNKNOWN;
  const displayedOceanFreightCostSource = transportPrice?.ocean_freight_cost?.source
    ? displayTransportCostSource(transportPrice.ocean_freight_cost.source)
    : UNKNOWN;
  const displayedDirectLandCostSource = transportPrice?.direct_land_cost?.source
    ? displayTransportCostSource(transportPrice.direct_land_cost.source)
    : UNKNOWN;

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Transport cost details
      </Typography>
      {errors.transport_price ? <Alert severity="error">{String(errors.transport_price.message)}</Alert> : null}
      {errors.missing_transport_price ? <Alert severity="warning">No estimated route found</Alert> : null}
      {errors.transit_time_estimate ? (
        <Alert severity="error">{String(errors.transit_time_estimate.message)}</Alert>
      ) : null}
      {errors.missing_transit_time_estimate ? (
        <Alert severity="warning">No transit time estimate available for the given route</Alert>
      ) : null}
      {transportPrice?.is_from_expired_quote ? (
        <Alert severity="error">
          The transport cost is based on an expired quote. Consider this a less accurate indication. P
        </Alert>
      ) : null}
      <MissingCostsAlert missingCosts={transportPrice?.missing_costs} />
      <Box marginTop={1}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Transport cost per load</TableCell>
              <TableCell data-testid="transport-estimator-shipping_cost_per_load">
                {displayedShippingCostPerLoad}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Total transport cost</TableCell>
              <TableCell data-testid="transport-estimator-total-shipping-cost">{displayedTotalShippingCost}</TableCell>
            </TableRow>
            {originAddress && transportPrice?.origin_road_cost ? (
              <TableRow>
                <TableCell>Origin road cost source</TableCell>
                <TableCell data-testid="transport-estimator-origin-road-cost-source">
                  {displayedOriginRoadCostSource}
                </TableCell>
                <TableCell>Quoted on: {transportPrice.origin_road_cost.quoted_on}</TableCell>
              </TableRow>
            ) : null}
            {destinationAddress && transportPrice?.destination_road_cost ? (
              <TableRow>
                <TableCell>Destination road cost source</TableCell>
                <TableCell data-testid="transport-estimator-destination-road-cost-source">
                  {displayedDestinationRoadCostSource}
                </TableCell>
                <TableCell>Quoted on: {transportPrice.destination_road_cost.quoted_on}</TableCell>
              </TableRow>
            ) : null}
            {originAddress && destinationAddress && transportPrice?.direct_land_cost ? (
              <TableRow>
                <TableCell>Direct land cost source</TableCell>
                <TableCell data-testid="transport-estimator-direct-land-cost-source">
                  {displayedDirectLandCostSource}
                </TableCell>
                <TableCell>As of: {transportPrice.direct_land_cost.quoted_on}</TableCell>
              </TableRow>
            ) : null}
            {transportPrice?.ocean_freight_cost ? (
              <TableRow>
                <TableCell>Ocean freight cost source</TableCell>
                <TableCell>{displayedOceanFreightCostSource}</TableCell>
                <TableCell>Quoted on: {transportPrice.ocean_freight_cost.quoted_on}</TableCell>
              </TableRow>
            ) : null}
            {transportPrice?.e2e_cost ? (
              <TableRow>
                <TableCell>Cost source</TableCell>
                <TableCell>{displayTransportCostSource(transportPrice.e2e_cost.source)}</TableCell>
                <TableCell>Quoted on: {transportPrice.e2e_cost.quoted_on}</TableCell>
              </TableRow>
            ) : null}
            {transitTimeEstimate?.estimated_transit_days_collection_to_final_delivery ? (
              <TableRow>
                <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                  Estimated transit time (full)
                  <Tooltip
                    title={
                      'Estimated total transit time from collection site to final delivery location. This will be used for financing cost estimation.'
                    }
                  >
                    <Box component="span" sx={{ alignItems: 'center', display: 'flex', ml: 0.5 }}>
                      <HelpOutlineOutlined />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell data-testid="transport-estimator-estimated-transit-time-collection-to-final-delivery">
                  {transitTimeEstimate.estimated_transit_days_collection_to_final_delivery} days
                </TableCell>
              </TableRow>
            ) : null}
            {transitTimeEstimate?.estimated_transit_days_requested_route ? (
              <TableRow>
                <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                  Estimated transit time (requested)
                  <Tooltip
                    title={
                      'Estimated transit time based on the location selected in the above form. For reference only.'
                    }
                  >
                    <Box component="span" sx={{ alignItems: 'center', display: 'flex', ml: 0.5 }}>
                      <HelpOutlineOutlined />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell data-testid="transport-estimator-estimated-transit-time">
                  {transitTimeEstimate.estimated_transit_days_requested_route} days
                </TableCell>
              </TableRow>
            ) : null}
            {transitTimeEstimate?.source_description ? (
              <TableRow>
                <TableCell>Transit time source</TableCell>
                <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                  {transitTimeEstimate.source_description === TransitTimeSourceExplanation.FULL_MATCH
                    ? 'Full match'
                    : transitTimeEstimate.source_description ===
                        TransitTimeSourceExplanation.CONTINENT_TO_CONTINENT_APPROXIMATION
                      ? 'Continent to Continent Fallback'
                      : 'Partial match'}
                  <Tooltip title={sourceExplanationEnumValueToString(transitTimeEstimate.source_description)}>
                    <Box component="span" sx={{ alignItems: 'center', display: 'flex', ml: 0.5 }}>
                      <HelpOutlineOutlined />
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
